import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FindSectionsApi, FindSectionsRequest, FindSectionsResponse } from '../find-sections-api';
import { GuestAwareHttpService } from '../../services/guest-aware-http-service';
import { formatTimeoutError } from '../../../utils/timeout-handler';
import { resolveApiPath } from '@/utils/api-path-resolver';
import { FactoryPsHttpSSE, type PsHttpSSE } from '@patsnap-utils/sse';
import { injectService } from '@patsnap/tahiti-core';

@injectable()
export class FindSectionsApiImpl extends GuestAwareHttpService implements FindSectionsApi {
    async findSectionsStream(request: FindSectionsRequest): Promise<PsHttpSSE<FindSectionsResponse>> {
        const sseClient = injectService<FactoryPsHttpSSE>(FactoryPsHttpSSE);
        const resolvedPath = await resolveApiPath('find_sections_sse');
        const baseURL = import.meta.env.DEV ? '' : (import.meta.env.VITE_TELECOM_API || '');
        const url = `${baseURL}${resolvedPath}`;

        return sseClient<FindSectionsResponse>()
            .init({
                url,
                data: {
                    task_id: request.task_id,
                },
                hasFinalMessage: () => true,
            });
    }

    async findSections(request: FindSectionsRequest): Promise<FindSectionsResponse> {
        try {
            console.debug(`[FindSectionsApi] Starting SSE request for task_id: ${request.task_id}`);

            let finalResult: FindSectionsResponse = {
                status: false,
                error_code: -1,
                error_msg: 'No data received from SSE',
                data: { sections: [] },
            };

            const stream = await this.findSectionsStream(request);

            const result = await new Promise<FindSectionsResponse>((resolve, reject) => {
                stream
                    .onOpen(() => {
                        console.debug('[FindSectionsApi] SSE connection opened');
                    })
                    .onMessage((event) => {
                        // event: SSEResponse<FindSectionsResponse>
                        if (event.data.status === 'FINISH') {
                            finalResult = {
                                status: true,
                                error_code: 0,
                                error_msg: '',
                                data: { sections: event.data.content.sections || [] },
                            };
                        }
                    })
                    .onFinish(() => {
                        console.debug('[FindSectionsApi] SSE finished');
                        resolve(finalResult);
                    })
                    .onError((err) => {
                        console.error('[FindSectionsApi] SSE error:', err);
                        reject(err);
                    })
                    .start()
                    .catch((err) => {
                        console.error('[FindSectionsApi] SSE start failed:', err);
                        reject(err);
                    });
            });

            console.debug('[FindSectionsApi] SSE request completed');
            return result;
        } catch (error) {
            const formattedError = formatTimeoutError(error, 'Find Sections API');
            console.error('[FindSectionsApi] Request failed:', formattedError);
            throw new Error(formattedError);
        }
    }
}
